import React, { useCallback, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useModalContext } from '@bcraft-frontend-lib/core'
import {
  Fields,
  getEstimateBlock,
  PaymentTypes,
  PaymentTypesType,
  QRCode,
  QRCodeTimer,
  useConvertAmount,
  useCurrentPayment,
  UseCurrentPaymentParams,
  usePaymentAmount,
} from '@bcraft-frontend-lib/payments'
import block from 'bem-cn'
import { useFormik } from 'formik'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'

import { defaultCurrency } from 'shared/constants'

import { selectUserData } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import CopyButtonHoc from 'components/HOC/CopyButtonHoc/CopyButtonHoc'

import Loader from 'components/Loader/Loader'
import { Button, ButtonSizes, Form, FormInput } from 'components/UIKit'

import { PaymentFieldGroup } from '../PaymentFieldGroup'
import { PaymentScreens } from '../PaymentScreens'
import { RequisitesWrapper } from '../RequisitesWrapper'
import { TemplateAmount } from '../TemplateAmount'

import './CurrentPayment.scss'

interface IPaymentDetails {
  selectedSystem: string
  paymentType: PaymentTypesType
  changeSystem: (system: string) => void
}

const b = block('current-payment')

export const CurrentPayment: React.FC<IPaymentDetails> = props => {
  const { selectedSystem, paymentType } = props

  const { replaceLastModal } = useModalContext()

  const user = useSelector(selectUserData, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).payment

  const replaceData = useMemo(
    () => ({
      fullName: user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
    }),
    [user?.email, user?.firstName, user?.lastName],
  )

  const successCallback: UseCurrentPaymentParams['successCallback'] = useCallback(() => {
    replaceLastModal(MODAL_ROUTE_URLS.SUCCESS_PAYMENT(paymentType))()
  }, [paymentType, replaceLastModal])

  const {
    isLoading,
    isConvertAmountLoading,
    initialValues,
    currentPaymentSystemData,
    currentSlug,
    additionSlug,
    canSubmitDepositFiles,
    convertedAmountData,
    paymentProofsData,
    qrCodeData,
    validate,
    handleClear,
    createTransfer,
    buildTransferPayload,
  } = useCurrentPayment({
    type: paymentType,
    selectedSystem,
    replaceData,
    successCallback,
  })

  const formik = useFormik({
    initialValues,
    onSubmit: values => {
      const payload = buildTransferPayload(values)
      if (!payload) {
        return
      }

      createTransfer(payload)
    },
    validate,
    enableReinitialize: true,
  })

  useConvertAmount({
    amount: formik.values.amount,
    currencyFrom: user?.currency,
    currencyTo: additionSlug,
  })

  const amountData = usePaymentAmount({
    error: formik.errors.amount,
    system: currentPaymentSystemData,
    additionSlug,
    userCurrency: user?.currency ?? defaultCurrency,
    isLoading: isConvertAmountLoading,
    setAmount: amount => formik.setFieldValue('amount', amount),
  })

  const limits = useMemo(
    () =>
      currentPaymentSystemData
        ? `${locale.min} ${currentPaymentSystemData?.min} ${user?.currency} /
      ${locale.max} ${currentPaymentSystemData?.max} ${user?.currency}`
        : null,
    [locale, currentPaymentSystemData, user],
  )

  const isDisabledButton = useMemo(
    () =>
      !formik.isValid || isConvertAmountLoading || (paymentType === PaymentTypes.DEPOSIT && !canSubmitDepositFiles),
    [formik.isValid, isConvertAmountLoading, paymentType, canSubmitDepositFiles],
  )

  const renderQrCode = () => {
    if (!qrCodeData) return null
    return (
      <div className={b('qrcode-container')}>
        <QRCode value={qrCodeData.url} className={b('qrcode')} />
        <div className={b('qrcode-text')}>
          {locale.payQrCode}
          <QRCodeTimer
            className={b('qrcode-time')}
            handleClear={handleClear}
          />
        </div>
        <RequisitesWrapper
          requisites={[{
            name: '', value: qrCodeData.url, isQrCode: false
          }]}
        />
        {handleClear && (
          <div className={b('qrcode-button')}>
            <Button onClick={handleClear} text={locale.cancelPayment} />
          </div>
        )}
      </div>
    )
  }

  const renderAmountField = () => {
    const { error: amountError, ...amountProps } = amountData
    const estimatedAmountBlock = () => {
      if (isConvertAmountLoading) {
        return <Loader colorType='dark' />
      }

      return getEstimateBlock(
        String(formik.values.amount),
        convertedAmountData?.estimatedAmount,
        additionSlug,
      )
    }

    console.log(convertedAmountData?.estimatedAmount)

    return (
      <>
        <div className={b('group')}>
          <label className={b('group-label')}>{locale.amount}</label>
          <FormInput
            name={amountProps.name}
            onChange={amountProps.setValue}
            suffix={amountProps.endAdornment}
            errorText={formik.touched.amount && amountError}
          />
          <div className={b('estimate-amount')}>
            {estimatedAmountBlock()}
          </div>
        </div>
        <TemplateAmount
          paymentType={paymentType}
          changeValue={value => formik.setFieldValue('amount', value)}
          slug={currentSlug}
        />
        <div className={b('group', { limits: true })}>
          <label>
            {limits && (
              <>
                {limits} <b>{locale.perTransaction}</b>
              </>
            )}
          </label>
        </div>
      </>
    )
  }

  const renderFieldsBlock = () => (
    <Fields fields={currentPaymentSystemData?.fields} className={b('fields')}>
      {({ field, title }) => (
        <PaymentFieldGroup
          key={`field-${title}`}
          title={title}
          field={field}
          touched={formik.touched}
          errors={formik.errors}
          value={formik.values[field.name]}
          onBlur={formik.handleBlur}
          setFieldValue={formik.setFieldValue}
        />
      )}
    </Fields>
  )

  return (
    <div className={b()}>
      {qrCodeData ? (
        renderQrCode()
      ) : (
        <Form formik={formik}>
          <RequisitesWrapper requisites={currentPaymentSystemData?.requisites} className={b('requisites')} />

          {renderAmountField()}

          {renderFieldsBlock()}

          {paymentType === PaymentTypes.DEPOSIT && (
            <PaymentScreens {...paymentProofsData} />
          )}

          <div className={b('buttons-wrapper')}>
            <div className={b('button-wrapper')}>
              <Button
                text={locale.transactionTypes[paymentType]}
                type="submit"
                disabled={isDisabledButton}
                loading={isLoading}
                fullWidth
                size={ButtonSizes.LARGE}
              />
            </div>
          </div>
        </Form>
      )}
    </div>
  )
}
